/**
 * The Auth0-backed consent provider: two POSTs to our own backend.
 *
 * **This file is the OneTrust seam.** Everything above it -- the machine, the store, the fingerprint,
 * the gate, the five exported functions -- knows only `{ read, write }` and never learns where the
 * boolean is stored. A `createOneTrustConsentProvider` is a sibling of this file, and the shape to
 * mirror is `resources/scripts/tracking/consent.js`'s `createConsentSource`, which is already an
 * adapter of exactly this kind (with a change-only feed and the `SPOptanonWrapperCalled` bridge
 * from includes-consent/consent-compliance.php).
 *
 * **Both calls are POSTs, including the read.** D-063 rules out a per-fan endpoint whose freshness
 * depends on a header, because one install stamps `public, max-age=3600` over every 200 it serves --
 * which is how a re-logged-in fan was handed the previous fan's token out of their own browser
 * cache. docs/gotchas.md states the remedy: a guarantee has to come from the caller, as
 * `cache: 'no-store'` or as a method nothing caches. This uses both, and the server refuses GET
 * entirely, so no cacheable variant of the payload exists.
 *
 * **It never sends an unauthenticated request.** No credential is a thrown
 * `LoyaltyConsentAuthError` before the wire, not an anonymous POST -- the same posture
 * session.js's `getAccessToken` docblock describes for self-scoped loyalty routes: raise the truth
 * rather than send a doomed request and read the answer as a mystery.
 *
 * Everything it touches is injected, so provider.test.js drives it with a fake transport under
 * `node --test` and no network.
 */

import { LoyaltyConsentAuthError, LoyaltyConsentError } from './errors.js';
import { withDeadline } from '../deadline.js';

/**
 * Deadlines, and there were none before 2026-09-25.
 *
 * A request that was accepted and never answered used to leave its promise pending forever, and
 * ./machine.js serialises writes (`writing = writing.then( run, run )`) -- so one hung write wedged
 * every later write in that tab, and a hung read left the gate chain unsettled for the life of the
 * page. The read allowance sits above the server's worst case with no object cache (a 5s token mint
 * then a 5s Management GET, `http_timeout` each); the write's is `write_deadline_ms` from config.
 *
 * The deadline wraps the CREDENTIAL lookup as well as the request, because the Auth0 SDK answers
 * asynchronously and can hang on its own.
 */
export const READ_TIMEOUT_MS = 15000;
export const WRITE_TIMEOUT_MS = 20000;

/**
 * Build a provider over an injected transport.
 *
 * @param {Object}                options                  Inputs.
 * @param {string}                options.statusUrl        Where to read. Root-relative.
 * @param {string}                options.setUrl           Where to write. Root-relative.
 * @param {Function}              options.transport        A `fetch`-shaped function.
 * @param {() => Promise<string>} options.getCredential    The fan's bearer token, or ''.
 * @param {number}                [options.readTimeoutMs]  Deadline for a read, credential included.
 * @param {number}                [options.writeTimeoutMs] Deadline for a write, credential included.
 * @param {Object}                [options.timers]         `{set, clear}`, for tests.
 * @return {{read: Function, write: Function}} The provider.
 */
export function createAuth0ConsentProvider( {
	statusUrl,
	setUrl,
	transport,
	getCredential,
	readTimeoutMs = READ_TIMEOUT_MS,
	writeTimeoutMs = WRITE_TIMEOUT_MS,
	timers,
} ) {
	/**
	 * One call, and the only place a request is built.
	 *
	 * @param {string}      url       Endpoint.
	 * @param {Object|null} body      JSON body, or null for none.
	 * @param {number}      timeoutMs How long the whole call may take, credential included.
	 * @return {Promise<{granted: boolean, checkedAt: number}>} The confirmed value.
	 */
	function send( url, body, timeoutMs ) {
		if ( ! url ) {
			return Promise.reject(
				new LoyaltyConsentError( 'Loyalty consent is not configured.' )
			);
		}

		return withDeadline( signal => authorisedSend( url, body, signal ), timeoutMs, {
			timers,
			// A plain consent error, never the auth one: a slow endpoint is not a missing fan, and
			// `requiresReauth` would send a fan with a perfectly good session to a login. `timedOut`
			// is what lets a caller tell "did not answer" -- where a write may still have landed --
			// from a refusal.
			makeError: ms =>
				Object.assign(
					new LoyaltyConsentError(
						`Loyalty consent endpoint did not answer within ${ ms }ms.`
					),
					{ timedOut: true }
				),
		} );
	}

	/**
	 * @param {string}      url    Endpoint.
	 * @param {Object|null} body   JSON body, or null for none.
	 * @param {AbortSignal} signal Aborted when the deadline runs out.
	 * @return {Promise<{granted: boolean, checkedAt: number}>} The confirmed value.
	 */
	function authorisedSend( url, body, signal ) {
		// **Asked for at the moment the request is built, and awaited.** The credential used to be
		// a synchronous cookie read; the Auth0 SDK answers asynchronously, from its own cache on any
		// ordinary page and with a fresh token when the cached one has expired. Resolving it here
		// rather than at wiring time is what keeps a long-lived provider from holding a stale
		// bearer. `Promise.resolve()` wraps it so a host that still hands over a synchronous
		// function keeps working.
		return Promise.resolve( typeof getCredential === 'function' ? getCredential() : '' ).then(
			credential => {
				if ( ! credential ) {
					return Promise.reject( new LoyaltyConsentAuthError() );
				}

				return request( url, credential, body, signal );
			}
		);
	}

	/**
	 * The request itself, once there is something to send it with.
	 *
	 * @param {string}      url        Endpoint.
	 * @param {string}      credential The fan's bearer.
	 * @param {Object|null} body       JSON body, or null for none.
	 * @param {AbortSignal} [signal]   Aborts the request when the deadline runs out.
	 * @return {Promise<{granted: boolean, checkedAt: number}>} The confirmed value.
	 */
	function request( url, credential, body, signal ) {
		return transport( url, {
			signal,
			method: 'POST',
			// Belt to the method's brace. The method is what actually guarantees this, but the
			// sentence in docs/gotchas.md names both, and the original defect was the browser's own
			// disk cache under `fetch`'s default `cache: 'default'`.
			cache: 'no-store',
			// No cookies. This endpoint is bearer-authenticated and reads none, which is also why it
			// is not CSRF-shaped: possession of an `Authorization` header proves script-level access
			// rather than mere cookie transmission.
			credentials: 'omit',
			headers: {
				Authorization: `Bearer ${ credential }`,
				'Content-Type': 'application/json',
				Accept: 'application/json',
			},
			body: JSON.stringify( body || {} ),
		} ).then( response => {
			// **Only a 401 is "sign in again".** The endpoint answers 401 for a token it refused and
			// for nothing else. Its 403 is `consent_origin_refused` -- a page on an origin the
			// install does not recognise -- and treating that as an auth failure sent the fan to
			// Auth0 on a write (`requiresReauth`), to come back to the same refusal. An unreadable
			// Auth0 key set used to arrive as a 401 too, with the same result; it is a 503 now
			// (`consent_keys_unavailable`), and a plain error below like every other outage.
			if ( response.status === 401 ) {
				return Promise.reject( new LoyaltyConsentAuthError() );
			}

			if ( ! response.ok ) {
				return Promise.reject(
					new LoyaltyConsentError(
						`Loyalty consent endpoint answered ${ response.status }.`
					)
				);
			}

			return response.json().then( payload => {
				// A 2xx whose `granted` is not a boolean means we do not know what is stored, and
				// coercing it would turn "unreadable" into a definite answer on a consent record.
				if ( ! payload || typeof payload.granted !== 'boolean' ) {
					return Promise.reject(
						new LoyaltyConsentError(
							'Loyalty consent endpoint answered an unreadable payload.'
						)
					);
				}

				const answer = {
					granted: payload.granted,
					checkedAt: Number( payload.checkedAt ) || 0,
				};

				// Present only when the server sent it (a read asks for it), so a caller that never
				// asked sees exactly the shape it always did.
				if ( 'membership' in payload ) {
					answer.membership = payload.membership;
				}

				return answer;
			} );
		} );
	}

	return {
		/**
		 * @return {Promise<{granted: boolean, checkedAt: number}>} The fan's current consent.
		 */
		read() {
			// `withMembership` rides the read the page makes anyway, so a pending join or leave is
			// learned at no extra request. The server adds the summary only when asked.
			return send( statusUrl, { withMembership: true }, readTimeoutMs );
		},

		/**
		 * Persist the fan's consent.
		 *
		 * The body carries **exactly** `granted`. There is no fan identifier in it, by design: the
		 * server takes the `sub` from the token's signature, and a field the contract does not have
		 * is a field no future reader can start honouring. provider.test.js asserts the body's shape
		 * for that reason.
		 *
		 * @param {boolean} granted Whether consent is being given.
		 * @return {Promise<{granted: boolean, checkedAt: number}>} What the server confirmed.
		 */
		write( granted ) {
			return send( setUrl, { granted: Boolean( granted ) }, writeTimeoutMs );
		},
	};
}
